import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import ms from 'ms'
import { useCallback, useMemo } from 'react'

import { useFeedbackIcon } from '@/lib/provider/FeedbackIconProvider'
import useToast from '@hooks/useToast.ts'
import useTypedTranslation from '@hooks/useTypedTranslation.ts'
import { type ProductWrapper, useProductMapper } from '@state/product.ts'
import { type FilterAndSort, FilterAndSortWrapper, filterList, sortTodoList, TodoItemWrapper } from '@state/util/todoList'
import useScanContextAwareHttpClient from '@utils/ScanContextAwareHttpClient.ts'
import useViewContextAwareHttpClient from '@utils/ViewContextAwareHttpClient'
import { useStockLevelMapper } from '@utils/stockLevelMapper.ts'

import type { components } from '../../schema'

export type TaskItemResponse = components['schemas']['TaskItemResponse']

export interface TransformedTaskItemResponse extends Omit<TaskItemResponse, 'product'> {
  product: ProductWrapper
}

export type ACTION_TYPE = TaskItemResponse['action']

export type TodoListResponse = TaskItemResponse[]
export type TransformedTodoListResponse = TodoItemWrapper[]

const todoListKeys = {
  list: (storeId: string, viewKey: string) => ['todo-list', storeId, viewKey] as const,
}

export function useTodoList(filterAndSort?: FilterAndSortWrapper) {
  const { get, storeId, viewKey } = useViewContextAwareHttpClient({ latestDayOnly: true })
  const productMapper = useProductMapper()
  const { map: stockLevelMapper } = useStockLevelMapper()

  return useQuery<TransformedTodoListResponse>({
    queryKey: todoListKeys.list(storeId, viewKey),
    queryFn: async () => {
      const response = await get<TodoListResponse>('/list/out-of-shelf')
      return response.map(item => {
        const product = productMapper(item.product)
        const stockLevel = stockLevelMapper(item.stockLevel)
        return new TodoItemWrapper({ ...item, product, stockLevel })
      })
    },
    staleTime: ms('5m'),
    select: useCallback((todoList: TransformedTodoListResponse) => {
      if (!filterAndSort) return todoList

      const filteredList = filterList(todoList, filterAndSort)
      return sortTodoList(filteredList, filterAndSort)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterAndSort ? filterAndSort.toString() : 'NONE']),
  })
}

export function useTodoItemByGtin(gtin: string | undefined) {
  const { data: list = [] } = useTodoList()

  return useMemo(() => {
    if (!gtin) return undefined

    return list.find(item => item.product.gtin === gtin)
  }, [gtin, list])
}

export function useTaskCountByFilter(filterAndSort: Partial<FilterAndSort>): number | undefined {
  const { data } = useTodoList(new FilterAndSortWrapper(filterAndSort))

  return data ? data.length : undefined
}

export function useTodoCount(): { countCompleted: number | undefined; countOpen: number | undefined } {
  const { data: todoList } = useTodoList()

  return useMemo(() => {
    if (!todoList) return { countCompleted: undefined, countOpen: undefined }

    const countCompleted = todoList.filter(entry => entry.isCompleted()).length
    const countOpen = todoList.length - countCompleted
    return { countCompleted, countOpen }
  }, [todoList])
}

function useUpdateTask(status: TaskItemResponse['status']) {
  const queryClient = useQueryClient()
  // a task has its own id: the backend ignores the scan id in this path, so a day needs no endpoint of its own
  const { put } = useScanContextAwareHttpClient()
  const { storeId, viewKey } = useViewContextAwareHttpClient({ latestDayOnly: true })
  const { showToast } = useToast()
  const { t } = useTypedTranslation()
  const { indicateSuccess } = useFeedbackIcon()

  return useMutation<undefined, Error, {taskId: string}>({
    mutationFn: ({ taskId }) => {
      return put(`/tasks/${taskId}`, { status: status })
    },
    onMutate: ({ taskId }) => {
      if (status === 'COMPLETED') {
        indicateSuccess()
      }

      // Optimistically updating the task status to OPEN/COMPLETED
      queryClient.setQueryData(todoListKeys.list(storeId, viewKey), (old: TodoItemWrapper[]) => {
        return old.map(item => {
          if (item.id !== taskId) return item

          // eslint-disable-next-line @typescript-eslint/no-misused-spread
          return new TodoItemWrapper({ ...item, status })
        })
      })
    },
    onError: () => {
      if (status === 'OPEN') {
        showToast({
          variant: 'error',
          title: t('todoList.toasts.completedError.title'),
          description: t('todoList.toasts.completedError.description'),
        })
      }

      if (status == 'COMPLETED') {
        showToast({
          variant: 'error',
          title: t('todoList.toasts.reopenError.title'),
          description: t('todoList.toasts.reopenError.description'),
        })
      }
    },
    onSettled: async () => {
      // trigger an invalidate which implies a rollback of the optimistic update if the mutation failed
      await queryClient.invalidateQueries({ queryKey: todoListKeys.list(storeId, viewKey) })
    },
  })
}

export function useCompleteTask() {
  return useUpdateTask('COMPLETED')
}

export function useReopenTask() {
  return useUpdateTask('OPEN')
}